// The erasure receipt (spec/dashboard.md section 11.3; spec/api-conventions.md section 17.3): when, by whom, why, the counts
// per store, the scope lines and the notes. Nothing personal is on it.
import { Link, useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { ErasureReceipt } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { useQuery } from '../../workspace/cache';
import { errorText } from './commands';
import './players.css';
import { LoadError, LoadingPane } from '../../ui/PageStates';
import { Lock, Receipt } from '../../ui/icons';

export function ReceiptPage() {
  const { env: envSlug = '', receipt: receiptId = '' } = useParams();
  const { org, game, can, offsets } = useGame();
  const { client } = useAuth();
  const [zone] = useTimeZone();
  const canRead = can('players_read', envSlug);
  const q = useQuery(canRead && receiptId ? `branch:${game.id}:env:${envSlug}:erasure:${receiptId}` : null, () => client.data(ops.getErasureReceipt, { path: { org, game: game.slug, env: envSlug, receipt: receiptId } }) as unknown as Promise<ErasureReceipt>);
  const back = (
    <p>
      <Link to={routes.players(org, game.slug, envSlug)}>Back to the players</Link>
    </p>
  );
  const r = q.data;
  const removed = r && r.removed && typeof r.removed === 'object' && !Array.isArray(r.removed) ? Object.entries(r.removed as Record<string, unknown>) : [];
  return (
    <section className="env-page players-page" data-testid="receipt">
      <div className="page-head">
        <h1>Erasure receipt</h1>
        <code className="page-meta">{receiptId}</code>
      </div>
      {!canRead ? (
        <EmptyState icon={<Lock />} title="You cannot read players here" body={`Reading the erasure receipts of “${envSlug}” needs the 'players_read' permission on this environment.`} />
      ) : q.error ? (
        q.error instanceof ApiError && q.error.status === 404 ? (
          <EmptyState icon={<Receipt />} title="No such receipt" body={<code>{receiptId}</code>} />
        ) : (
          <LoadError>{errorText(q.error)}</LoadError>
        )
      ) : !r ? (
        <LoadingPane rows={5} label="Loading the receipt" />
      ) : (
        <article className="pane receipt" aria-label="Erasure receipt">
          <div className="pane-head">
            <Receipt className="icon" aria-hidden="true" />
            <h2>Player erased</h2>
            <span className="spacer" />
            <span className="status-dot status-ended">Erased</span>
          </div>
          <dl className="kv pane-body receipt-facts">
            <dt>Receipt</dt>
            <dd>
              <code>{r.id}</code>
            </dd>
            <dt>Erased at</dt>
            <dd data-testid="receipt-erased-at">{formatInstantWithZone(r.erasedAt, zone, { offsets })}</dd>
            <dt>By</dt>
            <dd data-testid="receipt-actor">{r.actor?.label}</dd>
            <dt>Reason</dt>
            <dd data-testid="receipt-reason">{r.reason ?? '—'}</dd>
            <dt>Request id</dt>
            <dd>
              <code data-testid="receipt-request-id">{r.requestId}</code>
            </dd>
          </dl>
          <section className="pane-body receipt-section" aria-label="Removed">
            <h3>Removed</h3>
            <table className="table receipt-removed" data-testid="receipt-removed">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {removed.map(([store, count]) => (
                  <tr key={store} data-testid={`removed-${store}`}>
                    <td>{store}</td>
                    <td>{String(count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <section className="pane-body receipt-section" aria-label="Scope">
            <h3>Scope</h3>
            <ul data-testid="receipt-scope">
              {r.scope.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </section>
          {r.notes.length > 0 && (
            <section className="pane-body receipt-section" aria-label="Notes">
              <h3>Notes</h3>
              <ul data-testid="receipt-notes">
                {r.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </section>
          )}
        </article>
      )}
      {back}
    </section>
  );
}
